import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Description as DocIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import orgDocumentService from '../services/orgDocumentService';

const DOCUMENT_TYPES = [
  { value: 'FIELD_VALIDATION', label: 'Field Validation' },
  { value: 'DEV_GUIDELINES', label: 'Dev Guidelines' },
  { value: 'DESIGN_GUIDELINES', label: 'Design Guidelines' },
  { value: 'SECURITY_GUIDELINES', label: 'Security Guidelines' },
  { value: 'API_GUIDELINES', label: 'API Guidelines' },
  { value: 'CODING_STANDARDS', label: 'Coding Standards' },
];

const OrgDocuments = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('');

  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [formData, setFormData] = useState({
    document_type: 'FIELD_VALIDATION',
    title: '',
    description: '',
    version: '1.0',
    file: null,
  });
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    loadDocuments();
  }, [filterType]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const result = await orgDocumentService.getAll(filterType || null);
      setDocuments(result.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load organization documents');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUpload = (doc = null) => {
    if (doc) {
      setEditDoc(doc);
      setFormData({
        document_type: doc.document_type,
        title: doc.title,
        description: doc.description || '',
        version: doc.version || '1.0',
        file: null,
      });
    } else {
      setEditDoc(null);
      setFormData({
        document_type: 'FIELD_VALIDATION',
        title: '',
        description: '',
        version: '1.0',
        file: null,
      });
    }
    setUploadOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (editDoc) {
        const fd = new FormData();
        if (formData.title) fd.append('title', formData.title);
        if (formData.description) fd.append('description', formData.description);
        if (formData.version) fd.append('version', formData.version);
        await orgDocumentService.update(editDoc.id, fd);
      } else {
        const fd = new FormData();
        fd.append('document_type', formData.document_type);
        fd.append('title', formData.title);
        fd.append('description', formData.description);
        fd.append('version', formData.version);
        if (formData.file) {
          fd.append('file', formData.file);
        }
        await orgDocumentService.create(fd);
      }
      setUploadOpen(false);
      loadDocuments();
    } catch (err) {
      setError('Failed to save document');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await orgDocumentService.delete(deleteTarget.id);
      setDeleteTarget(null);
      loadDocuments();
    } catch (err) {
      setError('Failed to delete document');
      console.error(err);
    }
  };

  const handleToggleActive = async (doc) => {
    try {
      const fd = new FormData();
      fd.append('is_active', (!doc.is_active).toString());
      await orgDocumentService.update(doc.id, fd);
      loadDocuments();
    } catch (err) {
      setError('Failed to update document status');
      console.error(err);
    }
  };

  const getTypeLabel = (value) => {
    const found = DOCUMENT_TYPES.find((t) => t.value === value);
    return found ? found.label : value;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/settings')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Organization Documents
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage Infanion default documents used across all projects
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenUpload()}
          sx={{ textTransform: 'none', bgcolor: '#485869', '&:hover': { bgcolor: '#3a4a59' } }}
        >
          Upload Document
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter */}
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Type</InputLabel>
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            label="Filter by Type"
          >
            <MenuItem value="">All Types</MenuItem>
            {DOCUMENT_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : documents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <DocIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No organization documents found</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>File</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {doc.title}
                    </Typography>
                    {doc.description && (
                      <Typography variant="caption" color="text.secondary">
                        {doc.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={getTypeLabel(doc.document_type)} size="small" />
                  </TableCell>
                  <TableCell>{doc.version}</TableCell>
                  <TableCell>
                    <Typography variant="caption">{doc.original_filename || '—'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={doc.is_active}
                      onChange={() => handleToggleActive(doc)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {doc.created_at
                        ? new Date(doc.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenUpload(doc)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDeleteTarget(doc)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload / Edit Dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editDoc ? 'Edit Document' : 'Upload Organization Document'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {!editDoc && (
              <FormControl fullWidth>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={formData.document_type}
                  onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                  label="Document Type"
                >
                  {DOCUMENT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Version"
              value={formData.version}
              onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              fullWidth
            />
            {!editDoc && (
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                sx={{ textTransform: 'none' }}
              >
                {formData.file ? formData.file.name : 'Choose File (.txt, .md, .docx, .pdf, .xlsx, .csv)'}
                <input
                  type="file"
                  hidden
                  accept=".txt,.md,.docx,.pdf,.xlsx,.xls,.xlsm,.xlsb,.csv"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files[0] || null })}
                />
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formData.title}
            sx={{ textTransform: 'none', bgcolor: '#485869', '&:hover': { bgcolor: '#3a4a59' } }}
          >
            {saving ? <CircularProgress size={20} /> : editDoc ? 'Save Changes' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteTarget?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrgDocuments;
